import { hasTouch } from '../utils/touch';

// Scroll-linked keyframes, reading the skrollr attributes in the templates
// (replaces skrollr 0.6, abandoned):
//
//   data-600="opacity:0"   keyframe at scroll position 600
//   data-[offset]-<viewport anchor>[-<element anchor>]="…"
//                          anchors: top | center | bottom; the element anchor
//                          defaults to the viewport anchor. The keyframe is the
//                          scroll position where the anchors line up, minus the
//                          offset: data-200-bottom = 200px before the element's
//                          bottom reaches the viewport's bottom, data--500-top =
//                          500px after its top reaches the viewport's top.
//
// Numeric properties are interpolated linearly between keyframes and held beyond
// the first and last one. As with skrollr's smooth scrolling, the effect follows
// the scroll position over 200ms (square-root easing).
// Desktop only (no touch), as before; <html> gets .scroll-keyframes, which the
// CSS uses for the layouts built around these effects.

const ATTRIBUTE = /^data-(-?\d+)?-?(top|center|bottom)?(?:-(top|center|bottom))?$/;
const ANCHOR = {top: 0, center: 0.5, bottom: 1};
const SMOOTHING = 200; // ms

const parseProps = (value) => Object.fromEntries(value.split(';')
    .map((p) => p.split(':').map((s) => s.trim()))
    .filter(([prop, v]) => prop && v !== undefined && !Number.isNaN(parseFloat(v)))
    .map(([prop, v]) => [prop, parseFloat(v)]));

function keyframeDefinitions(el) {
    return [...el.attributes].flatMap(({name, value}) => {
        const match = name.match(ATTRIBUTE);
        if (!match || (match[1] === undefined && !match[2]) || !value.includes(':')) return [];
        const [, offset = '0', viewportAnchor, elementAnchor] = match;
        return [{offset: parseInt(offset, 10), viewportAnchor, elementAnchor: elementAnchor ?? viewportAnchor, props: parseProps(value)}];
    });
}

function scrollPosition({offset, viewportAnchor, elementAnchor}, el) {
    if (!viewportAnchor) return offset;
    const rect = el.getBoundingClientRect();
    const viewportHeight = document.documentElement.clientHeight;
    const aligned = rect.top + window.scrollY + rect.height * ANCHOR[elementAnchor] - viewportHeight * ANCHOR[viewportAnchor];
    return Math.round(aligned) - offset;
}

function valueAt(frames, prop, top) {
    const withProp = frames.filter((f) => prop in f.props);
    const first = withProp[0], last = withProp[withProp.length - 1];
    if (top <= first.at) return first.props[prop];
    if (top >= last.at) return last.props[prop];
    const next = withProp.findIndex((f) => f.at > top);
    const a = withProp[next - 1], b = withProp[next];
    return a.props[prop] + (b.props[prop] - a.props[prop]) * (top - a.at) / (b.at - a.at);
}

export default function scrollKeyframes() {
    if (hasTouch()) return;

    const items = [...document.querySelectorAll('body *')]
        .map((el) => ({el, definitions: keyframeDefinitions(el)}))
        .filter(({definitions}) => definitions.length);
    if (!items.length) return;

    document.documentElement.classList.add('scroll-keyframes');

    // Keyframe positions depend on the layout: computed now and on resize/load.
    const measure = () => items.forEach((item) => {
        item.frames = item.definitions
            .map((d) => ({at: scrollPosition(d, item.el), props: d.props}))
            .sort((a, b) => a.at - b.at);
        item.propNames = [...new Set(item.frames.flatMap((f) => Object.keys(f.props)))];
    });

    const render = (top) => items.forEach(({el, frames, propNames}) => {
        propNames.forEach((prop) => el.style.setProperty(prop, valueAt(frames, prop, top)));
    });

    let rendered = window.scrollY;
    let from = rendered, diff = 0, start = 0, frame = null;

    const tick = (now) => {
        // rAF timestamps can be slightly earlier than `start` (performance.now()).
        const t = Math.min(Math.max((now - start) / SMOOTHING, 0), 1);
        rendered = Math.round(from + Math.sqrt(t) * diff);
        render(rendered);
        frame = t < 1 ? requestAnimationFrame(tick) : null;
    };

    const follow = () => {
        from = rendered;
        diff = window.scrollY - rendered;
        start = performance.now();
        if (!frame) frame = requestAnimationFrame(tick);
    };

    measure();
    render(rendered);
    window.addEventListener('scroll', follow, {passive: true});
    ['resize', 'load'].forEach((type) => window.addEventListener(type, () => {
        measure();
        render(rendered);
    }));
}
